import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { apiClient } from '@/shared/lib/axios';
import { clinicApiPath } from '@/shared/lib/apiPath';
import { useClinicContext } from './useClinicContext';
import { useTheme } from '@/shared/providers/theme/ThemeContext';
import type {
  DocumentTemplateInitResponse,
  DocumentTemplateListResponse,
  DocumentTemplateDetailResponse,
  DocumentTemplateFormData,
  DocumentTemplateSearchParams,
  DocumentTemplateMutationResponse,
  DocumentTemplateDeleteResponse,
  BatchUpdateItem,
  BatchUpdateResponse,
} from '@/types/documentTemplate.types';

// ==================== API Service Functions ====================

const getInit = async (siteType: string, clinicNumber: string, serviceType: string): Promise<DocumentTemplateInitResponse> => {
  const response = await apiClient.get<DocumentTemplateInitResponse>(
    clinicApiPath(siteType, clinicNumber, '/document-templates/init'),
    { headers: { 'X-Service-Type': serviceType } }
  );
  return response.data;
};

const getList = async (
  siteType: string,
  clinicNumber: string,
  serviceType: string,
  params?: DocumentTemplateSearchParams
): Promise<DocumentTemplateListResponse> => {
  const response = await apiClient.get<DocumentTemplateListResponse>(
    clinicApiPath(siteType, clinicNumber, '/document-templates'),
    { params, headers: { 'X-Service-Type': serviceType } }
  );
  return response.data;
};

const getDetail = async (siteType: string, clinicNumber: string, serviceType: string, id: number): Promise<DocumentTemplateDetailResponse> => {
  const response = await apiClient.get<DocumentTemplateDetailResponse>(
    clinicApiPath(siteType, clinicNumber, `/document-templates/${id}`),
    { headers: { 'X-Service-Type': serviceType } }
  );
  return response.data;
};

const create = async (siteType: string, clinicNumber: string, serviceType: string, data: DocumentTemplateFormData): Promise<DocumentTemplateMutationResponse> => {
  const response = await apiClient.post<DocumentTemplateMutationResponse>(
    clinicApiPath(siteType, clinicNumber, '/document-templates'),
    data,
    { headers: { 'X-Service-Type': serviceType } }
  );
  return response.data;
};

const update = async (siteType: string, clinicNumber: string, serviceType: string, id: number, data: Partial<DocumentTemplateFormData>): Promise<DocumentTemplateMutationResponse> => {
  const response = await apiClient.put<DocumentTemplateMutationResponse>(
    clinicApiPath(siteType, clinicNumber, `/document-templates/${id}`),
    data,
    { headers: { 'X-Service-Type': serviceType } }
  );
  return response.data;
};

const deleteTemplate = async (siteType: string, clinicNumber: string, serviceType: string, id: number): Promise<DocumentTemplateDeleteResponse> => {
  const response = await apiClient.delete<DocumentTemplateDeleteResponse>(
    clinicApiPath(siteType, clinicNumber, `/document-templates/${id}`),
    { headers: { 'X-Service-Type': serviceType } }
  );
  return response.data;
};

const batchUpdate = async (
  siteType: string,
  clinicNumber: string,
  serviceType: string,
  templates: BatchUpdateItem[]
): Promise<BatchUpdateResponse> => {
  const response = await apiClient.post<BatchUpdateResponse>(
    clinicApiPath(siteType, clinicNumber, '/document-templates/batch-update'),
    { templates },
    { headers: { 'X-Service-Type': serviceType } }
  );
  return response.data;
};

// ==================== Hooks ====================

/**
 * Hook to get initialization data
 */
export const useMainDocumentTemplateInit = () => {
  const { tenant, clinicNumber } = useClinicContext();
  const { serviceType } = useTheme();

  return useQuery({
    queryKey: ['document-templates', 'init', tenant, clinicNumber, serviceType],
    queryFn: () => getInit(tenant, clinicNumber, serviceType),
    enabled: !!tenant && !!clinicNumber,
  });
};

/**
 * Hook to get paginated list of templates.
 * serviceType in queryKey ensures refetch when user switches R2 ↔ A2.
 */
export const useMainDocumentTemplates = (params?: DocumentTemplateSearchParams) => {
  const { tenant, clinicNumber } = useClinicContext();
  const { serviceType } = useTheme();

  return useQuery({
    queryKey: ['document-templates', 'list', tenant, clinicNumber, serviceType, params],
    queryFn: () => getList(tenant, clinicNumber, serviceType, params),
    enabled: !!tenant && !!clinicNumber,
    placeholderData: keepPreviousData,
  });
};

/**
 * Hook to get template detail
 */
export const useMainDocumentTemplateDetail = (id: number) => {
  const { tenant, clinicNumber } = useClinicContext();
  const { serviceType } = useTheme();

  return useQuery({
    queryKey: ['document-templates', 'detail', tenant, clinicNumber, serviceType, id],
    queryFn: () => getDetail(tenant, clinicNumber, serviceType, id),
    enabled: !!tenant && !!clinicNumber && !!id,
  });
};

/**
 * Hook to create a template
 */
export const useMainDocumentTemplateCreate = () => {
  const queryClient = useQueryClient();
  const { tenant, clinicNumber } = useClinicContext();
  const { serviceType } = useTheme();

  return useMutation({
    mutationFn: (data: DocumentTemplateFormData) => create(tenant, clinicNumber, serviceType, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-templates'] });
    },
  });
};

/**
 * Hook to update a template
 */
export const useMainDocumentTemplateUpdate = () => {
  const queryClient = useQueryClient();
  const { tenant, clinicNumber } = useClinicContext();
  const { serviceType } = useTheme();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<DocumentTemplateFormData> }) =>
      update(tenant, clinicNumber, serviceType, id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['document-templates'] });
      queryClient.invalidateQueries({ queryKey: ['document-templates', 'detail', variables.id] });
    },
  });
};

/**
 * Hook to delete a template
 */
export const useMainDocumentTemplateDelete = () => {
  const queryClient = useQueryClient();
  const { tenant, clinicNumber } = useClinicContext();
  const { serviceType } = useTheme();

  return useMutation({
    mutationFn: (id: number) => deleteTemplate(tenant, clinicNumber, serviceType, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-templates'] });
    },
  });
};

/**
 * Hook for batch update of practitioner details
 */
export const useMainDocumentTemplateBatchUpdate = () => {
  const queryClient = useQueryClient();
  const { tenant, clinicNumber } = useClinicContext();
  const { serviceType } = useTheme();

  return useMutation({
    mutationFn: (templates: BatchUpdateItem[]) => batchUpdate(tenant, clinicNumber, serviceType, templates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-templates'] });
    },
  });
};
